/**
 * PersonalSection — name, DOB, shirt size, address, SSN (encrypted),
 * and identity-document uploads.
 */
import React, { useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  IdentificationCard, Star, Calendar, Sparkle, House, Fingerprint,
  Eye, EyeSlash, Lock,
} from '@phosphor-icons/react';
import { OnboardingPublicAPI } from '../../api/onboardingApi';
import {
  Field, TextInput, US_STATES, SHIRT_SIZES, inputBase, inputStyle,
} from './primitives';
import { IdentityDocs } from './IdentityDocs';
import { ShirtPreview } from './ShirtPreview';

export function PersonalSection({ form, setEvt, set, isLight }) {
  return (
    <div className="space-y-4" data-testid="section-personal">
      <Field label="Full legal name" required icon={<IdentificationCard size={11} weight="fill" />} testId="field-fullname">
        <TextInput value={form.full_name} onChange={setEvt('full_name')} placeholder="Jordan Smith" autoComplete="name" isLight={isLight} testId="input-fullname" />
      </Field>
      <Field label="Preferred name (optional)" icon={<Star size={11} weight="fill" />} testId="field-preferred">
        <TextInput value={form.preferred_name} onChange={setEvt('preferred_name')} placeholder="Jay" isLight={isLight} testId="input-preferred" />
      </Field>
      <Field label="Date of birth" icon={<Calendar size={11} weight="fill" />} testId="field-dob"
        hint="Used to confirm you're 18+ for retail employment.">
        <TextInput type="date" value={form.date_of_birth} onChange={setEvt('date_of_birth')} isLight={isLight} testId="input-dob" />
      </Field>
      <Field label="Shirt size" icon={<Sparkle size={11} weight="fill" />} testId="field-shirt">
        <ShirtPreview size={form.shirt_size} />
        <div className="grid grid-cols-7 gap-1.5" data-testid="shirt-size-grid">
          {SHIRT_SIZES.map(sz => {
            const active = form.shirt_size === sz;
            return (
              <button key={sz} type="button"
                onClick={() => set('shirt_size')(sz)}
                className="py-2 rounded-xl text-xs font-black transition-all"
                style={{
                  background: active ? 'linear-gradient(135deg, #CD040B, #F59E0B)'
                                     : (isLight ? '#FFFFFF' : 'rgba(10,10,14,0.6)'),
                  color: active ? '#fff' : (isLight ? '#0f172a' : '#F5F5F5'),
                  border: `1px solid ${active ? 'transparent' : (isLight ? '#E2E8F0' : 'rgba(255,255,255,0.10)')}`,
                  boxShadow: active ? '0 6px 16px -8px rgba(245,158,11,0.5)' : 'none',
                }}
                data-testid={`shirt-size-${sz}`}>
                {sz}
              </button>
            );
          })}
        </div>
      </Field>
      <Field label="Home address" icon={<House size={11} weight="fill" />} testId="field-address">
        <div className="space-y-2">
          <TextInput value={form.address_line1} onChange={setEvt('address_line1')} placeholder="Street address" autoComplete="address-line1" isLight={isLight} testId="input-address1" />
          <TextInput value={form.address_line2} onChange={setEvt('address_line2')} placeholder="Apt, suite, unit (optional)" autoComplete="address-line2" isLight={isLight} testId="input-address2" />
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <div className="sm:col-span-2"><TextInput value={form.city} onChange={setEvt('city')} placeholder="City" autoComplete="address-level2" isLight={isLight} testId="input-city" /></div>
            <StateSelect value={form.state} onChange={set('state')} isLight={isLight} />
            <TextInput value={form.zip_code} onChange={setEvt('zip_code')} placeholder="ZIP" autoComplete="postal-code" isLight={isLight} testId="input-zip" />
          </div>
        </div>
      </Field>
      <SSNField form={form} set={set} isLight={isLight} />
      <IdentityDocs form={form} set={set} isLight={isLight} />
    </div>
  );
}

function StateSelect({ value, onChange, isLight }) {
  return (
    <select
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      className={inputBase + ' focus:border-[#F59E0B] cursor-pointer appearance-none'}
      style={{
        ...inputStyle(isLight),
        backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23${isLight ? '64748b' : 'a3a3a3'}'><path d='M7 10l5 5 5-5z'/></svg>")`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'right 10px center',
        paddingRight: 32,
      }}
      autoComplete="address-level1"
      data-testid="input-state">
      <option value="">State</option>
      {US_STATES.map(([code, name]) => (
        <option key={code} value={code}>{`${code} — ${name}`}</option>
      ))}
    </select>
  );
}

function SSNField({ form, set, isLight }) {
  const [raw, setRaw] = useState('');
  const [reveal, setReveal] = useState(false);
  const [encState, setEncState] = useState('idle');
  const [error, setError] = useState('');
  const debounceRef = useRef(null);

  const fmt = (digits) => {
    if (!digits) return '';
    if (digits.length <= 3) return digits;
    if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5, 9)}`;
  };

  const onChange = (e) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 9);
    setRaw(digits);
    setError('');
    setEncState('idle');
    set('ssn_encrypted')('');
    set('ssn_last4')('');
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (digits.length === 9) {
      debounceRef.current = setTimeout(async () => {
        setEncState('encrypting');
        try {
          const r = await OnboardingPublicAPI.encryptSSN(digits);
          set('ssn_encrypted')(r.ssn_encrypted);
          set('ssn_last4')(r.ssn_last4);
          setEncState('done');
        } catch (err) {
          setEncState('error');
          setError(err.message || 'Encryption failed');
        }
      }, 350);
    }
  };

  return (
    <Field label="Social Security Number" required icon={<Fingerprint size={11} weight="fill" />} testId="field-ssn"
      hint="Encrypted in our servers using AES (Fernet).">
      <div className="relative" style={{ perspective: 1000 }}>
        <AnimatePresence mode="wait">
          <motion.div
            key={reveal ? 'reveal' : 'hidden'}
            initial={{ rotateY: reveal ? -90 : 90, opacity: 0 }}
            animate={{ rotateY: 0, opacity: 1 }}
            exit={{ rotateY: reveal ? 90 : -90, opacity: 0 }}
            transition={{ duration: 0.32, ease: [0.2, 0.9, 0.3, 1] }}
            style={{ transformStyle: 'preserve-3d' }}>
            <input
              type={reveal ? 'text' : 'password'}
              value={reveal ? fmt(raw) : raw}
              onChange={onChange}
              inputMode="numeric"
              autoComplete="off"
              placeholder="XXX-XX-XXXX"
              className={inputBase + ' pr-24 focus:border-[#EF4444] tracking-wider'}
              style={{ ...inputStyle(isLight), letterSpacing: '0.08em' }}
              data-testid="input-ssn"
            />
          </motion.div>
        </AnimatePresence>
        <button type="button" onClick={() => setReveal(r => !r)}
          className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-black z-10"
          style={{ background: 'transparent', color: 'var(--cs-text-muted)' }}
          data-testid="ssn-toggle-reveal">
          {reveal ? <><EyeSlash size={11} weight="bold" /> Hide</> : <><Eye size={11} weight="bold" /> Show</>}
        </button>
      </div>
      <div className="flex items-center gap-2 text-[10px] mt-1">
        <Lock size={10} weight="fill" style={{ color: '#10B981' }} />
        {encState === 'idle' && raw.length > 0 && raw.length < 9 &&
          <span style={{ color: 'var(--cs-text-muted)' }}>Keep typing… {raw.length}/9</span>}
        {encState === 'encrypting' && <span style={{ color: '#F59E0B' }}>Encrypting…</span>}
        {encState === 'done' && <span style={{ color: '#10B981' }}>Encrypted ✓ stored as ***-**-{form.ssn_last4}</span>}
        {encState === 'error' && <span style={{ color: '#EF4444' }}>{error || 'Could not encrypt — try again'}</span>}
        {encState === 'idle' && raw.length === 0 &&
          <span style={{ color: 'var(--cs-text-muted)' }}>Encrypted on submit · never logged in plaintext</span>}
      </div>
    </Field>
  );
}
